import React, { useState } from "react";
import "../styles/add-team-member-modal.css";
import "../styles/program-document-modal.css";
import "../styles/program-video-modal.css";
import { createProgramVideo, updateProgramVideo } from "../services/programVideoApi";

type EditTarget = {
    id: number;
    name: string;
    videoUrl: string;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    companyId?: number | null;
    editVideo?: EditTarget | null;
};

function validateName(n: string): string | null {
    const t = n.trim();
    if (t.length < 5 || t.length > 50) {
        return "Video name must be between 5 and 50 characters.";
    }
    return null;
}

function validateVideoUrl(raw: string): string | null {
    const t = raw.trim();
    if (!t) {
        return "Video URL is required.";
    }
    try {
        const u = new URL(t);
        if (u.protocol !== "http:" && u.protocol !== "https:") {
            return "Video URL must use http or https.";
        }
        if (!u.hostname) {
            return "Video URL must include a valid host.";
        }
    } catch {
        return "Enter a valid URL.";
    }
    return null;
}

export default function ProgramVideoModal({ open, onClose, onSuccess, companyId = null, editVideo = null }: Props) {
    const [name, setName] = useState("");
    const [videoUrl, setVideoUrl] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    React.useEffect(() => {
        if (open) {
            setName(editVideo?.name ?? "");
            setVideoUrl(editVideo?.videoUrl ?? "");
            setError(null);
            setSubmitting(false);
        }
    }, [open, editVideo]);

    if (!open) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const nameErr = validateName(name);
        if (nameErr) {
            setError(nameErr);
            return;
        }
        const urlErr = validateVideoUrl(videoUrl);
        if (urlErr) {
            setError(urlErr);
            return;
        }

        setError(null);
        setSubmitting(true);
        try {
            if (editVideo) {
                await updateProgramVideo(editVideo.id, {
                    name: name.trim(),
                    videoUrl: videoUrl.trim(),
                });
            } else {
                await createProgramVideo({
                    name: name.trim(),
                    videoUrl: videoUrl.trim(),
                    companyId,
                });
            }
            onSuccess();
            onClose();
        } catch (err: unknown) {
            const any = err as { message?: string; body?: { detail?: string } };
            setError(any?.body?.detail || any?.message || "Something went wrong.");
        } finally {
            setSubmitting(false);
        }
    };

    const title = editVideo ? "Edit Video" : "Add Video";
    const submitLabel = editVideo ? "Save changes" : "Add video";

    return (
        <div className="team-modal" role="dialog" aria-modal="true" aria-labelledby="program-video-modal-title">
            <div className="team-modal-card team-modal-card--confirm program-document-modal-card program-video-modal-card">
                <button type="button" className="team-modal-close" onClick={onClose} aria-label="Close">
                    ×
                </button>
                <form onSubmit={handleSubmit} className="team-modal-content team-modal-content--confirm">
                    <h2 id="program-video-modal-title" className="team-modal-confirm-title">
                        {title}
                    </h2>

                    <div className="program-document-field">
                        <label htmlFor="program-video-name" className="program-document-label">
                            Video name
                        </label>
                        <input
                            id="program-video-name"
                            type="text"
                            className="program-document-input"
                            placeholder="Video Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={submitting}
                            autoComplete="off"
                        />
                    </div>

                    <div className="program-document-field">
                        <label htmlFor="program-video-url" className="program-video-link-label">
                            Add link to video
                        </label>
                        <input
                            id="program-video-url"
                            type="url"
                            className="program-document-input"
                            placeholder="https://"
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                            disabled={submitting}
                            autoComplete="off"
                        />
                    </div>

                    {error && (
                        <div className="program-document-error" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="program-document-actions">
                        <button type="submit" className="program-document-submit" disabled={submitting}>
                            {submitting ? "Working…" : submitLabel}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
